import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Tag, Popconfirm, message, Modal, Form, Select, Drawer, Descriptions, Image } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, ExportOutlined, ImportOutlined, EyeOutlined } from '@ant-design/icons';
import useCompanyNavigate from '../../utils/useCompanyNavigate';
import { productApi, storeApi, batchApi, traceApi } from '../../services/api';
import dayjs from 'dayjs';

// 日期格式化（YYYY-MM-DD，无值显示-）
const fmtDate = (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '-');

const inventoryStatusMap = {
  1: { text: '在库', color: 'green' },
  2: { text: '已售出', color: 'red' },
  3: { text: '退库中', color: 'orange' }
};

const Products = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [keyword, setKeyword] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState(undefined);
  const [batches, setBatches] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(undefined);
  const [stores, setStores] = useState([]);
  // 查看溯源
  const [viewOpen, setViewOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [distributeModalVisible, setDistributeModalVisible] = useState(false);
  const [distributeLoading, setDistributeLoading] = useState(false);
  const [distributeForm] = Form.useForm();
  const navigate = useCompanyNavigate();

  useEffect(() => {
    fetchData(1);
    fetchStores();
    fetchBatches();
  }, []);

  const fetchData = async (page = 1, searchKeyword = keyword, batchId = selectedBatchId, storeId = selectedStoreId) => {
    setLoading(true);
    try {
      const res = await productApi.list({
        page,
        limit: pagination.pageSize,
        keyword: searchKeyword,
        batchId: batchId || undefined,
        storeId: storeId || undefined
      });
      setData(res.data.list);
      setPagination({
        ...pagination,
        current: page,
        total: res.data.pagination.total
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBatches = async () => {
    try {
      const res = await batchApi.list({ limit: 1000 });
      setBatches(res.data.list || []);
    } catch {}
  };

  const fetchStores = async () => {
    try {
      const res = await storeApi.list({ status: 1, limit: 100 });
      if (res.success) {
        setStores([{ id: 0, name: '工厂' }, ...res.data.list]);
      }
    } catch (error) {
      console.error('获取门店列表失败', error);
    }
  };

  const handleSearch = () => {
    fetchData(1, keyword);
  };

  // 查看溯源信息（复用公开溯源接口，不含视频）
  const openView = async (record) => {
    setViewing(record);
    setViewOpen(true);
    setViewData(null);
    setViewLoading(true);
    try {
      const res = await traceApi.query(record.product_code);
      setViewData(res.data);
    } catch {
      message.error('获取溯源信息失败');
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await productApi.delete(id);
      message.success('删除成功');
      fetchData(pagination.current);
    } catch {
      // 错误已在拦截器处理
    }
  };

  // 批量分配 — 只有在工厂的在库货品才能分配
  const handleDistribute = async () => {
    const factoryRows = selectedRows.filter(r => r.is_factory && r.inventory_status === 1);
    if (factoryRows.length === 0) {
      message.warning('没有可分配的货品（仅工厂在库货品可分配）');
      return;
    }
    distributeForm.resetFields();
    setDistributeModalVisible(true);
  };

  const handleDistributeSubmit = async () => {
    try {
      const values = await distributeForm.validateFields();
      setDistributeLoading(true);
      // 只提交在工厂的货品
      const factoryProductIds = selectedRows
        .filter(r => r.is_factory && r.inventory_status === 1)
        .map(r => r.id);
      await productApi.distribute({
        product_ids: factoryProductIds,
        store_id: values.store_id
      });
      message.success('分配成功');
      setDistributeModalVisible(false);
      setSelectedRows([]);
      fetchData(pagination.current);
    } catch (error) {
      message.error('分配失败');
    } finally {
      setDistributeLoading(false);
    }
  };

  // 只有工厂在库的货品可以多选
  const rowSelection = {
    selectedRowKeys: selectedRows.map(r => r.id),
    onChange: (_, selectedRows) => {
      setSelectedRows(selectedRows);
    },
    getCheckboxProps: (record) => ({
      disabled: !record.is_factory || record.inventory_status !== 1
    })
  };

  // 可分配的货品数量
  const distributableCount = selectedRows.filter(r => r.is_factory && r.inventory_status === 1).length;

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '货品编码',
      dataIndex: 'product_code',
      key: 'product_code',
      render: (text) => <code style={{ color: '#8B7355' }}>{text}</code>
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: '批次',
      dataIndex: 'batch_no',
      key: 'batch_no',
      render: (text) => <Tag color="default">{text}</Tag>
    },
    {
      title: '品类',
      dataIndex: 'category',
      key: 'category',
      render: (text) => text || '-'
    },
    {
      title: '材质',
      dataIndex: 'material',
      key: 'material',
      render: (text) => text || '-'
    },
    {
      title: '所在门店',
      key: 'store',
      render: (_, record) => {
        const storeName = record.is_factory ? '工厂' : (record.store_name || '-');
        const statusInfo = inventoryStatusMap[record.inventory_status] || {};
        return (
          <Space direction="vertical" size={0}>
            <span>{storeName}</span>
            {record.inventory_status && <Tag color={statusInfo.color} style={{ fontSize: 11 }}>{statusInfo.text}</Tag>}
          </Space>
        );
      }
    },
    {
      title: '照片',
      dataIndex: 'photo_count',
      key: 'photo_count',
      render: (count) => count > 0 ? <Tag color="blue">{count} 张</Tag> : '-'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => openView(record)}
          >
            查看
          </Button>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/admin/products/${record.id}/edit`)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此货品吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2>货品管理</h2>
        <Space>
          {distributableCount > 0 && (
            <Button
              icon={<ExportOutlined />}
              onClick={handleDistribute}
            >
              批量分配 ({distributableCount})
            </Button>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/admin/products/new')}
            style={{ background: '#8B7355' }}
          >
            新增货品
          </Button>
          <Button
            icon={<ImportOutlined />}
            onClick={() => navigate('/admin/products/import')}
          >
            批量导入
          </Button>
        </Space>
      </div>

      <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
        <Input.Search
          placeholder="搜索货品编码或名称"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={handleSearch}
          onPressEnter={handleSearch}
          style={{ width: 300 }}
          prefix={<SearchOutlined />}
          allowClear
        />
        <Select
          placeholder="按批次筛选"
          value={selectedBatchId}
          onChange={(val) => {
            setSelectedBatchId(val);
            fetchData(1, keyword, val);
          }}
          allowClear
          showSearch
          filterOption={(input, option) => option.label?.toLowerCase().includes(input.toLowerCase())}
          style={{ width: 240 }}
          options={batches.map(b => ({ label: `${b.batch_no} - ${b.name}`, value: b.id }))}
        />
        <Select
          placeholder="按门店筛选"
          value={selectedStoreId}
          onChange={(val) => {
            setSelectedStoreId(val);
            fetchData(1, keyword, selectedBatchId, val);
          }}
          allowClear
          showSearch
          filterOption={(input, option) => option.label?.toLowerCase().includes(input.toLowerCase())}
          style={{ width: 200 }}
          options={stores.filter(s => s.id !== 0).map(s => ({ label: s.name, value: s.id }))}
        />
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          showTotal: (total) => `共 ${total} 条`
        }}
        onChange={(p) => fetchData(p.current)}
      />

      {/* 批量分配弹框 */}
      <Modal
        title="批量分配货品"
        open={distributeModalVisible}
        onCancel={() => setDistributeModalVisible(false)}
        onOk={handleDistributeSubmit}
        confirmLoading={distributeLoading}
      >
        <div style={{ marginBottom: 16 }}>
          <strong>可分配货品（工厂在库）：</strong> {selectedRows.filter(r => r.is_factory && r.inventory_status === 1).map(r => r.product_code).join(', ')}
        </div>
        <Form form={distributeForm} layout="vertical">
          <Form.Item
            name="store_id"
            label="分配到门店"
            rules={[{ required: true, message: '请选择门店' }]}
          >
            <Select placeholder="请选择门店">
              {stores.filter(s => s.name !== '工厂').map(store => (
                <Select.Option key={store.id} value={store.id}>
                  {store.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 查看溯源信息抽屉 */}
      <Drawer
        title={`溯源信息 — ${viewing?.product_code || ''}`}
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        width={640}
      >
        {viewLoading && <p style={{ textAlign: 'center', padding: 40, color: '#999' }}>加载中...</p>}
        {viewData && (
          <>
            <Descriptions title="基本信息" column={2} size="small" bordered>
              <Descriptions.Item label="编码">{viewData.product?.code}</Descriptions.Item>
              <Descriptions.Item label="名称">{viewData.product?.name || '-'}</Descriptions.Item>
              <Descriptions.Item label="品类">{viewData.product?.category || '-'}</Descriptions.Item>
              <Descriptions.Item label="材质">{viewData.product?.material || '-'}</Descriptions.Item>
              <Descriptions.Item label="重量(g)">{viewData.product?.weight ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="尺寸">{viewData.product?.dimensions || '-'}</Descriptions.Item>
              <Descriptions.Item label="工艺师">{viewData.product?.craftsMan || '-'}</Descriptions.Item>
              <Descriptions.Item label="完工日期">{fmtDate(viewData.product?.finishDate)}</Descriptions.Item>
              <Descriptions.Item label="描述" span={2}>{viewData.product?.description || '-'}</Descriptions.Item>
            </Descriptions>

            {viewData.jadeType && (
              <Descriptions title="料种信息" column={2} size="small" bordered style={{ marginTop: 24 }}>
                <Descriptions.Item label="料种">{viewData.jadeType.name || '-'}</Descriptions.Item>
                <Descriptions.Item label="产地">{viewData.jadeType.origin || '-'}</Descriptions.Item>
              </Descriptions>
            )}

            <Descriptions title="时间信息" column={2} size="small" bordered style={{ marginTop: 24 }}>
              <Descriptions.Item label="原石日期">{fmtDate(viewData.rawMaterialDate)}</Descriptions.Item>
              <Descriptions.Item label="入库日期">{fmtDate(viewData.rawMaterialEntryDate)}</Descriptions.Item>
              <Descriptions.Item label="生产开始">{fmtDate(viewData.productionStartDate)}</Descriptions.Item>
              <Descriptions.Item label="生产结束">{fmtDate(viewData.productionEndDate)}</Descriptions.Item>
            </Descriptions>

            <div style={{ marginTop: 24, fontWeight: 600 }}>照片</div>
            {(() => {
              const photosByType = viewData.photos?.photosByType || {};
              const nameMap = {};
              [...(viewData.batchPhotoTypes || []), ...(viewData.productPhotoTypes || [])].forEach(t => {
                nameMap[String(t.id)] = t.name;
              });
              const entries = Object.entries(photosByType).filter(([, ps]) => ps && ps.length > 0);
              if (entries.length === 0) return <p style={{ color: '#999' }}>暂无照片</p>;
              return entries.map(([typeId, photos]) => (
                <div key={typeId} style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 500, marginBottom: 8 }}>{nameMap[typeId] || `类型${typeId}`}</div>
                  <Image.PreviewGroup>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {photos.map(p => (
                        <Image key={p.id} src={p.url} width={100} height={100} style={{ objectFit: 'cover' }} />
                      ))}
                    </div>
                  </Image.PreviewGroup>
                </div>
              ));
            })()}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default Products;
